<template>
  <div class="mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <div class="dialog__body">
        <p :id="titleId" class="dialog__lead">{{ $t("proofreadingIncompleteThemeDialog.message") }}</p>
        <p class="dialog__lead">{{ $t("proofreadingIncompleteThemeDialog.detail") }}</p>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="dialog__ok-button" @click="onClickOk">
          {{ $t("proofreadingIncompleteThemeDialog.ok") }}
        </button>
        <button type="button" class="dialog__theme-button" @click="onClickEditTheme">
          {{ $t("proofreadingIncompleteThemeDialog.editTheme") }}
        </button>
        <button type="button" class="dialog__cancel-button" @click="onClickCancel">
          {{ $t("proofreadingIncompleteThemeDialog.cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export type ProofreadingIncompleteThemeDialogResult = "ok" | "editTheme" | "cancel";

export default Vue.extend({
  data() {
    return {
      titleId: "proofreading-incomplete-theme-dialog-title",
    };
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      this.onClickCancel();
    },
    onClickOk() {
      this.$close("ok" as ProofreadingIncompleteThemeDialogResult);
    },
    onClickEditTheme() {
      this.$close("editTheme" as ProofreadingIncompleteThemeDialogResult);
    },
    onClickCancel() {
      this.$close("cancel" as ProofreadingIncompleteThemeDialogResult);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9300;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);

  &:focus {
    outline: none;
  }
}

.dialog__body {
  padding: 24px 24px 8px;
}

.dialog__lead {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
  text-align: left;

  & + & {
    margin-top: 8px;
  }
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px 24px;
}

.dialog__cancel-button,
.dialog__theme-button,
.dialog__ok-button {
  position: relative;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}

.dialog__ok-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}

.dialog__theme-button,
.dialog__cancel-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}
</style>
